// Light decomposition (PR 14 ST 7 Q2 — extract row only; orchestrator
// stays in StaffDashboard.jsx). Presentational + controlled: parent
// owns mutations and passes typed callbacks.

import { Button } from "@shared/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/components/ui/alert-dialog";
import { Pencil, Trash2, QrCode } from "lucide-react";
import type { ExhibitListItem } from "@shared/api/exhibits";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

interface ExhibitListRowProps {
  exhibit: ExhibitListItem;
  onTogglePublish: (id: string, state: ExhibitListItem["state"]) => void;
  onGenerateQR: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const ExhibitListRow = ({
  exhibit,
  onTogglePublish,
  onGenerateQR,
  onEdit,
  onDelete,
}: ExhibitListRowProps) => {
  const isPublished = exhibit.state === "published";
  const isArchived = exhibit.state === "archived";
  const isPurged = exhibit.state === "purged";
  const isInactive = isArchived || isPurged;

  return (
    <tr data-testid={`exhibit-row-${exhibit.id}`}>
      <td className="px-6 py-4">
        <div className="font-mono text-2xl font-bold text-primary">
          {exhibit.code || "---"}
        </div>
      </td>
      <td className="px-6 py-4">
        {exhibit.coverImageId ? (
          <img
            src={`${API}/assets/${exhibit.coverImageId}`}
            alt={exhibit.titleTc}
            className="w-16 h-16 object-cover rounded"
            data-testid={`exhibit-image-${exhibit.id}`}
            onError={(e) => {
              // Asset bytes gone (purged or storage drift) — hide the
              // broken-image icon and let the placeholder show through.
              e.currentTarget.style.display = "none";
              const placeholder = e.currentTarget.nextElementSibling;
              if (placeholder instanceof HTMLElement)
                placeholder.style.display = "flex";
            }}
          />
        ) : null}
        <div
          className="w-16 h-16 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground"
          style={{ display: exhibit.coverImageId ? "none" : "flex" }}
        >
          No image
        </div>
      </td>
      <td className="px-6 py-4">
        <div
          className="font-medium"
          data-testid={`exhibit-title-tc-${exhibit.id}`}
        >
          {exhibit.titleTc}
        </div>
        <div className="text-sm text-muted-foreground">{exhibit.titleEn}</div>
      </td>
      <td className="px-6 py-4">
        {isArchived ? (
          <span
            className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
            title="Restore the parent exhibition to bring this exhibit back"
            data-testid={`publish-toggle-${exhibit.id}`}
          >
            ▼ Archived
          </span>
        ) : isPurged ? (
          <span
            className="px-3 py-1 rounded-full text-xs font-medium bg-zinc-200 text-zinc-600"
            title="Exhibit has been purged — tombstone only, asset bytes removed"
            data-testid={`publish-toggle-${exhibit.id}`}
          >
            ✕ Purged
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onTogglePublish(exhibit.id, exhibit.state)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              isPublished
                ? "bg-green-100 text-green-700"
                : "bg-zinc-100 text-zinc-700"
            }`}
            data-testid={`publish-toggle-${exhibit.id}`}
          >
            {isPublished ? "Published" : "Unpublished"}
          </button>
        )}
      </td>
      <td className="px-6 py-4">
        {exhibit.qrCodeUrl ? (
          <span
            className="text-green-600 text-sm"
            data-testid={`qr-status-${exhibit.id}`}
          >
            Generated
          </span>
        ) : isInactive ? (
          <span className="text-muted-foreground text-sm">—</span>
        ) : (
          <Button
            onClick={() => onGenerateQR(exhibit.id)}
            variant="outline"
            size="sm"
            data-testid={`generate-qr-${exhibit.id}`}
          >
            <QrCode className="w-4 h-4 mr-1" />
            Generate
          </Button>
        )}
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => onEdit(exhibit.id)}
            variant="ghost"
            size="sm"
            data-testid={`edit-button-${exhibit.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          {!isInactive && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  data-testid={`delete-button-${exhibit.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Exhibit</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(exhibit.id)}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ExhibitListRow;
